import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import { imageurl } from "../../App";
import { brandLogo } from "../../assets/img/logo/brandLogos";
import { loadStoreSettings } from "../../util/storeSettings";
import {
  FaArrowLeft,
  FaPrint,
  FaFileInvoiceDollar,
  FaShoppingBag
} from "react-icons/fa";

export default function OrderInvoice() {
  const { orderId } = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [invoiceDetails, setInvoiceDetails] = useState([]);
  const [total, setTotal] = useState(0);
  const [order, setOrder] = useState([]);
  const [storeSettings, setStoreSettings] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);

    // Fetch dynamic store configuration & branding
    loadStoreSettings()
      .then((settings) => {
        if (settings && (settings.storeName || settings.logoUrl)) {
          setStoreSettings(settings);
        }
      })
      .catch(() => {});

    const search = new URLSearchParams(location.search);
    let tracking = search.get("tracking") || "";
    if (!tracking) {
      try {
        const saved = JSON.parse(sessionStorage.getItem("last_order_details") || "null");
        if (saved && String(saved.orderId) === String(orderId)) {
          tracking = saved.trackingNo || "";
        }
      } catch (e) {
        tracking = "";
      }
    }
    const trackingQuery = tracking ? `?tracking=${encodeURIComponent(tracking)}` : "";

    const invoiceEndpoint = sessionStorage.getItem("auth_token")
      ? `/api/fetch-invoice-details/${orderId}`
      : `/api/user-invoice-details/${orderId}${trackingQuery}`;

    axios
      .get(invoiceEndpoint)
      .then((res) => {
        if (res.data.status === 200 && res.data.data) {
          setInvoiceDetails(res.data.data.orderDetails || []);
          setTotal(res.data.data.total_products_cost || 0);
          setOrder(res.data.data.order || []);
          document.title = `Invoice #${orderId}`;
        } else {
          // Try user-invoice-details as secondary fallback
          axios
            .get(`/api/user-invoice-details/${orderId}${trackingQuery}`)
            .then((secondRes) => {
              if (secondRes.data.status === 200 && secondRes.data.data) {
                setInvoiceDetails(secondRes.data.data.orderDetails || []);
                setTotal(secondRes.data.data.total_products_cost || 0);
                setOrder(secondRes.data.data.order || []);
                document.title = `Invoice #${orderId}`;
              } else {
                setErrorMsg(res.data.message || "Order invoice details not available.");
              }
              setLoading(false);
            })
            .catch(() => {
              setErrorMsg("Unable to retrieve invoice for this order.");
              setLoading(false);
            });
          return;
        }
        setLoading(false);
      })
      .catch(() => {
        // Fallback attempt for guest
        axios
          .get(`/api/user-invoice-details/${orderId}${trackingQuery}`)
          .then((res) => {
            if (res.data.status === 200 && res.data.data) {
              setInvoiceDetails(res.data.data.orderDetails || []);
              setTotal(res.data.data.total_products_cost || 0);
              setOrder(res.data.data.order || []);
              document.title = `Invoice #${orderId}`;
            } else {
              setErrorMsg("Unable to load invoice record.");
            }
            setLoading(false);
          })
          .catch(() => {
            setErrorMsg("Unable to load invoice record.");
            setLoading(false);
          });
      });
  }, [orderId, location.search]);

  if (loading) {
    return (
      <div className="py-5 text-center my-5">
        <div className="spinner-border text-primary" role="status"></div>
        <p className="text-muted mt-3 fw-semibold">Loading your invoice...</p>
      </div>
    );
  }

  if (errorMsg || order.length === 0) {
    return (
      <div className="container py-5 text-center my-5">
        <div className="p-5 bg-white rounded-4 border shadow-sm max-w-lg mx-auto">
          <FaFileInvoiceDollar size={48} className="text-muted mb-3 opacity-50" />
          <h4 className="fw-bold mb-2">Invoice Not Found</h4>
          <p className="text-muted mb-4">{errorMsg || `Could not find an invoice for Order #${orderId}`}</p>
          <div className="d-flex justify-content-center gap-2">
            <Link to="/account" className="btn btn-outline-secondary rounded-pill px-4">
              My Orders
            </Link>
            <Link to="/collection" className="btn btn-primary rounded-pill px-4">
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const orderInfo = order[0] || {};
  const currencySymbol = storeSettings?.currencySymbol || "Rs.";
  const storeName = storeSettings?.storeName || "Store";
  const supportPhone = storeSettings?.supportPhone || "";
  const warehouseCity = storeSettings?.warehouseCity || "Karachi";
  const country = storeSettings?.country || "Pakistan";
  const storeLogo = storeSettings?.logoUrl
    ? (storeSettings.logoUrl.startsWith("http")
        ? storeSettings.logoUrl
        : `${imageurl}/${storeSettings.logoUrl.replace(/^\/+/, "")}`)
    : brandLogo;

  const formatDate = (dateVal) => {
    if (dateVal && typeof dateVal === "string" && !dateVal.startsWith("0000") && moment(dateVal).isValid()) {
      return moment(dateVal).format("DD MMMM YYYY, hh:mm A");
    }
    return moment().format("DD MMMM YYYY");
  };

  const originalItemsSubtotal = invoiceDetails.reduce(
    (sum, item) => sum + Number(item.product_price || 0) * Number(item.product_qty || 1),
    0
  );
  const totalCatalogDiscount = invoiceDetails.reduce(
    (sum, item) => sum + Number(item.discount_given || 0),
    0
  );
  const couponDisc = Number(orderInfo.coupon_discount || 0);
  const paidSubtotal = Number(total || (originalItemsSubtotal - totalCatalogDiscount));
  const shippingFee = Number(orderInfo.shipping_fee || 0);
  const grandTotal = Math.max(0, paidSubtotal + shippingFee - couponDisc);

  return (
    <div className="py-4 py-md-5" style={{ backgroundColor: "#f8fafc", minHeight: "90vh" }}>
      <div className="container" style={{ maxWidth: "860px" }}>
        {/* Navigation & Print Actions (Hidden when printing) */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4 d-print-none">
          <div className="d-flex align-items-center gap-2">
            <Link to="/account" className="btn btn-sm btn-light border rounded-pill px-3 shadow-sm">
              <FaArrowLeft className="me-1" /> My Account
            </Link>
            <span className="text-muted">/</span>
            <span className="fw-semibold text-dark" style={{ fontSize: "14px" }}>
              Invoice #{orderId}
            </span>
          </div>

          <div className="d-flex align-items-center gap-2">
            <Link
              to="/collection"
              className="btn btn-sm btn-outline-dark rounded-pill px-3 fw-semibold shadow-sm"
            >
              <FaShoppingBag className="me-1" /> Shop more
            </Link>
            <button
              type="button"
              onClick={() => window.print()}
              className="btn btn-sm btn-primary rounded-pill px-4 py-2 fw-bold d-flex align-items-center gap-2 shadow-sm"
            >
              <FaPrint size={13} /> Print invoice
            </button>
          </div>
        </div>

        <div
          className="bg-white p-4 p-md-5 rounded-4 border shadow-sm"
          style={{ borderColor: "#e2e8f0" }}
          id="customer-invoice"
        >
          <div className="d-flex justify-content-between align-items-start border-bottom pb-4 mb-4 flex-wrap gap-3">
            <div>
              <img
                src={storeLogo}
                alt={storeName}
                style={{ maxHeight: "48px", maxWidth: "180px", objectFit: "contain" }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = brandLogo;
                }}
              />
              <p className="text-muted mb-0 mt-2" style={{ fontSize: "13px", lineHeight: 1.5 }}>
                <strong className="text-dark d-block">{storeName}</strong>
                {warehouseCity}, {country}
                {supportPhone ? <span> · {supportPhone}</span> : null}
              </p>
            </div>
            <div className="text-md-end">
              <h2 className="fw-bold mb-1" style={{ fontSize: "22px", letterSpacing: "-0.03em", color: "#0f172a" }}>
                Invoice
              </h2>
              <span className="fw-bold d-block" style={{ fontSize: "14px" }}>#{orderId}</span>
              {orderInfo.tracking_no ? (
                <span className="d-block mt-1" style={{ fontSize: "14px" }}>
                  Tracking <strong>{orderInfo.tracking_no}</strong>
                </span>
              ) : null}
              <small className="text-muted d-block mt-1">{formatDate(orderInfo.created_at)}</small>
            </div>
          </div>

          <div className="row g-4 mb-4">
            <div className="col-12 col-md-7">
              <span className="text-muted fw-semibold d-block mb-1" style={{ fontSize: "12px" }}>
                Deliver to
              </span>
              <div className="fw-bold">{orderInfo.firstname} {orderInfo.lastname}</div>
              <p className="text-muted mb-0" style={{ fontSize: "13.5px", lineHeight: 1.6 }}>
                {orderInfo.address}<br />
                {orderInfo.city ? `${orderInfo.city}, ` : ""}{orderInfo.state || country}<br />
                {orderInfo.phone}
                {orderInfo.email && !String(orderInfo.email).includes("@guest.") ? <span> · {orderInfo.email}</span> : null}
              </p>
            </div>
            <div className="col-12 col-md-5 text-md-end">
              <span className="text-muted fw-semibold d-block mb-1" style={{ fontSize: "12px" }}>
                Payment
              </span>
              <div style={{ fontSize: "14px" }}>
                {orderInfo.payment_mode === "BT" ? "Bank transfer" : orderInfo.payment_mode || "Cash on Delivery"}
              </div>
            </div>
          </div>

          <div className="table-responsive mb-4">
            <table className="table align-middle mb-0">
              <thead style={{ fontSize: "12px", color: "#64748b" }}>
                <tr>
                  <th className="border-0 ps-0">Item</th>
                  <th className="border-0 text-center" style={{ width: "70px" }}>Qty</th>
                  <th className="border-0 text-end" style={{ width: "120px" }}>Price</th>
                  <th className="border-0 text-end pe-0" style={{ width: "130px" }}>Amount</th>
                </tr>
              </thead>
              <tbody style={{ fontSize: "14px" }}>
                {invoiceDetails.map((item, idx) => (
                  <tr key={idx}>
                    <td className="ps-0">{item.product_name}</td>
                    <td className="text-center">{item.product_qty}</td>
                    <td className="text-end">{currencySymbol} {Number(item.product_price).toLocaleString()}</td>
                    <td className="text-end pe-0 fw-semibold">{currencySymbol} {Number(item.total_price).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-end">
            <div style={{ width: "280px", fontSize: "14px" }}>
              <div className="d-flex justify-content-between py-1">
                <span className="text-muted">Subtotal</span>
                <span>{currencySymbol} {paidSubtotal.toLocaleString()}</span>
              </div>
              {couponDisc > 0 && (
                <div className="d-flex justify-content-between py-1">
                  <span className="text-muted">Discount</span>
                  <span>-{currencySymbol} {couponDisc.toLocaleString()}</span>
                </div>
              )}
              <div className="d-flex justify-content-between py-1">
                <span className="text-muted">Delivery</span>
                <span>{shippingFee === 0 ? "Free" : `${currencySymbol} ${shippingFee.toLocaleString()}`}</span>
              </div>
              <div className="d-flex justify-content-between py-2 mt-1 border-top">
                <span className="fw-bold">Total</span>
                <span className="fw-bold" style={{ fontSize: "18px" }}>{currencySymbol} {grandTotal.toLocaleString()}</span>
              </div>
            </div>
          </div>

          <p className="text-center text-muted mb-0 mt-4 pt-3 border-top" style={{ fontSize: "13px" }}>
            Thank you for shopping with {storeName}.
          </p>
        </div>
      </div>
    </div>
  );
}
